import {
  BpkStack,
  BpkSpacing,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { addDays } from 'date-fns/addDays';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { useSelector } from 'react-redux';

import { useLocalisation } from '../../../../services/contextAdaptor/LocalisationContext';
import { useTranslation } from '../../helpers/hooks/useTranslation';
import {
  getSelectedHotelCheckInDate,
  getSelectedHotelCheckOutDate,
  getTotalHotelsPriceFromSelection,
} from '../../selectors';
import getSelectedDaysPrice from '../../selectors/selectedDaysPrice';

import type { State } from '../../selectors/types';

const TotalCostEstimate = () => {
  const { formatCurrency } = useLocalisation();
  const { tWithElements } = useTranslation();

  // Use the same price selector as the calendar so the total cost estimate
  // always matches the price shown for the selected date(s), including when
  // directness and/or departure time filters are applied.
  const flightPrice = useSelector(getSelectedDaysPrice);
  const checkinDate = useSelector((state: State) =>
    getSelectedHotelCheckInDate(state),
  );
  const checkoutDateRaw = useSelector((state: State) =>
    getSelectedHotelCheckOutDate(state),
  );
  const days = checkoutDateRaw
    ? differenceInCalendarDays(checkoutDateRaw, checkinDate)
    : 0;
  const checkoutDate =
    checkoutDateRaw && days > 0 ? checkoutDateRaw : addDays(checkinDate, 1);

  const totalHotelsPrice = useSelector((state: State) =>
    getTotalHotelsPriceFromSelection(state, checkinDate, checkoutDate),
  );

  const totalTripPrice =
    flightPrice && totalHotelsPrice ? flightPrice + totalHotelsPrice : null;

  const totalTripPriceFormatted = totalTripPrice
    ? formatCurrency(totalTripPrice)
    : null;

  if (!totalTripPriceFormatted || !checkoutDateRaw || days === 0) {
    return null;
  }

  return (
    <BpkStack
      direction="row"
      justify="flex-end"
      marginTop={BpkSpacing.Base}
      gap={BpkSpacing.None}
      data-testid="total-cost-estimate"
    >
      <BpkText textStyle={TEXT_STYLES.footnote}>
        {tWithElements('ktxt_total_trip_estimated', {
          price: (
            <BpkText tagName="span" textStyle={TEXT_STYLES.label1}>
              {totalTripPriceFormatted}
            </BpkText>
          ),
        })}
      </BpkText>
    </BpkStack>
  );
};

export default TotalCostEstimate;
